import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import { tokens } from "../../theme/tokens";
import { graphqlEndpoint } from "../../config";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useResolvedTenantId } from "../../hooks/useResolvedTenantId";
import { READ_PROJECTS_FILTER } from "../../graph/projects/queries";
import { RESUBMIT_SOP_APPROVAL } from "../../graph/projects/mutations";
import CompactPreview from "../../components/CompactPreview";

const client = new GraphQLClient(graphqlEndpoint);

export default function RejectedSOPApprovalsView() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const { setSnackbar } = useSnackbar();
  const {
    user,
    tenantId,
    tenantName,
  } = useResolvedTenantId();

  const loggedInUserId = user?.id;

  const [rejectedSops, setRejectedSops] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRejectedSops = async () => {
    if (!tenantId) {
      setRejectedSops([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { readProjectsFilter } = await client.request(READ_PROJECTS_FILTER, {
        filter: {
          tenantId,
          isSOPTemplate: true,
        },
      });

      setRejectedSops(
        (readProjectsFilter || []).filter(
          (sop) => sop.approvalStatus === "REJECTED"
        )
      );
    } catch (err) {
      console.error("Failed to load rejected SOPs", err);

      setRejectedSops([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load rejected SOPs",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRejectedSops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handleResubmit = async (projectId) => {
    try {
      await client.request(RESUBMIT_SOP_APPROVAL, {
        input: {
          projectId,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "SOP resubmitted for approval",
      });

      await loadRejectedSops();
    } catch (err) {
      console.error("Failed to resubmit SOP approval", err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to resubmit SOP approval",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header title="REJECTED SOPs" subtitle="Tenant could not be resolved" />
        <Typography mt={3}>
          A tenant must be available before viewing rejected SOPs.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Header title="REJECTED SOPs" subtitle="Loading rejected SOPs" />
        <Typography mt={3}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title="REJECTED SOPs"
        subtitle={
          tenantName
            ? `SOPs rejected during approval for ${tenantName}`
            : "SOPs rejected during approval"
        }
      />

      {rejectedSops.length === 0 ? (
        <Box mt={3}>
          <Typography variant="h6">No rejected SOPs</Typography>
          <Typography color="text.secondary" mt={1}>
            Rejected SOPs will appear here when an approver requests changes.
          </Typography>
        </Box>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))"
          mt={2}
        >
          {rejectedSops.map((sop) => {
            const rejectedAssignments = (sop.approvalAssignments || []).filter(
              (assignment) => assignment.status === "REJECTED"
            );

            return (
              <Paper
                key={sop.id}
                sx={{
                  p: 2,
                  border: `1px solid ${colors.scalpelTeal[400]}`,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  {sop.title}
                </Typography>

                <Box sx={{ mb: 1 }}>
                  <Chip size="small" label="REJECTED" color="error" />
                </Box>

                <CompactPreview lines={8}>
                                    {sop.description || "No description"}
                                  </CompactPreview>

                {rejectedAssignments.length > 0 ? (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Rejection feedback
                    </Typography>

                    {rejectedAssignments.map((assignment) => (
                      <Box key={assignment.id} sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "inline-block",
                            mb: 0.75,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: "background.paper",
                            fontWeight: 700,
                            color: colors.scalpelTeal[300],
                            textTransform: "capitalize",
                          }}
                        >
                          {assignment.approverUser
                            ? `${assignment.approverUser?.user?.firstName} ${assignment.approverUser?.user?.lastName}`
                            : "Approver"}
                        </Typography>

                        <Typography variant="body2">
                          {assignment.rejectionReason || "No reason provided"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Rejection feedback
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No rejection feedback found.
                    </Typography>
                  </Box>
                )}

                <Box display="flex" justifyContent="space-between" gap={1} mt={1}>
                  <ActionButton
                    tone="success"
                    onClick={() =>
                      navigate(`/sops/edit/${sop.id}`, {
                        state: { redirectPath: "/sops/rejected" },
                      })
                    }
                  >
                    Edit SOP
                  </ActionButton>

                  <ActionButton
                    tone="warning"
                    onClick={() => handleResubmit(sop.id)}
                  >
                    Resubmit
                  </ActionButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}